import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import * as authActions from "../Authentication/redux/actions";

/**
 * The fund master path. It has been fronted by the API gateway for as long as the fund list has
 * existed, which is what makes it usable as the carrier below.
 */
const FUNDS_PATH = "/api/v1/admin-actions/dashboard/funds";

/**
 * Whether to call the NAV reads on their own routes.
 *
 * be-admin registers five dedicated routes (src/controller/nav/AdminNavController.ts), but each of
 * them is a NEW path, and a new path answers nothing until someone creates the matching API gateway
 * resource - the caller just gets the gateway's own 401/403/404. Until those resources exist, the
 * same five reads are served from FUNDS_PATH, which is already fronted, selected by `nav_view`.
 *
 * Default is the carrier form, because that is the one that works today. Set
 * REACT_APP_NAV_DEDICATED_ROUTES=true in the environment once the gateway resources are in place;
 * both forms hit the same service methods in be-admin, so nothing else has to change.
 */
const USE_DEDICATED_NAV_ROUTES =
  process.env.REACT_APP_NAV_DEDICATED_ROUTES === "true";

function navEndpoint(dedicatedPath, view) {
  return USE_DEDICATED_NAV_ROUTES
    ? { path: dedicatedPath, params: {} }
    : { path: FUNDS_PATH, params: { nav_view: view } };
}

/**
 * Read endpoints of the NAV sync. Each entry is a descriptor rather than a bare string so that
 * switching between the two forms above is one flag here and no change at any call site.
 */
export const NAV_PATHS = {
  history: navEndpoint("/api/v1/admin-actions/dashboard/nav/history", "history"),
  historySummary: navEndpoint(
    "/api/v1/admin-actions/dashboard/nav/history/summary",
    "history-summary"
  ),
  topHolding: navEndpoint(
    "/api/v1/admin-actions/dashboard/nav/top-holding",
    "top-holding"
  ),
  syncState: navEndpoint(
    "/api/v1/admin-actions/dashboard/nav/sync-state",
    "sync-state"
  ),
  navFunds: navEndpoint("/api/v1/admin-actions/dashboard/nav/funds", "funds"),
  // The fund master itself. Called with no page parameter it returns every fund, which is what the
  // coverage view needs in order to name the funds that have no NAV history at all. No nav_view,
  // so this is the endpoint's original behaviour.
  fundMaster: { path: FUNDS_PATH, params: {} },
  // A write, not a nav_view read: Portal No7 since inception for the fund_no the caller passes.
  // be-admin runs it inside the request and allows one dashboard sync at a time (409 otherwise).
  fundNavHistorySync: {
    path: "/api/v1/admin-actions/dashboard/syncFundsNAV",
    params: { job: "nav-history", backfill: "true" }
  },
  // Deletes the fund_no's NAV history rows, between the optional date_from and date_to the caller
  // passes. Shares the sync's one-at-a-time flag in be-admin, so it also answers 409 while one runs.
  fundNavHistoryDelete: {
    path: "/api/v1/admin-actions/dashboard/syncFundsNAV",
    params: { job: "nav-history-delete" }
  }
};

/** Page size the NAV tables are requested with. The backend clamps anything above 200. */
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Builds a query string, dropping anything the caller left empty so an absent filter really is
 * absent rather than an empty string the DTO would have to validate.
 */
export function buildQueryString(params) {
  const parts = [];
  Object.keys(params).forEach(key => {
    const value = params[key];
    if (value === undefined || value === null || value === "") {
      return;
    }
    parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
  });
  return parts.length > 0 ? "?" + parts.join("&") : "";
}

/** A picked date as the API's YYYY-MM-DD, or undefined so buildQueryString drops it. */
export function toApiDate(date) {
  return date ? moment(date).format("YYYY-MM-DD") : undefined;
}

/**
 * Thrown for any response the page cannot render. It carries the HTTP status so the error block
 * can say what actually happened instead of showing an empty table.
 */
export class NavRequestError extends Error {
  constructor(status, detail) {
    super(detail || "NAV request failed");
    this.name = "NavRequestError";
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Returns a request function for the NAV endpoints.
 *
 * There is no axios interceptor in this dashboard: every caller attaches the admin headers by hand
 * and handles 401 itself (see FundList/redux/saga.js). This hook is that boilerplate in one place.
 * A 401 signs the operator out and resolves to `{ signedOut: true }` so the caller stops quietly
 * instead of rendering an error the operator will never see.
 */
export function useNavRequest() {
  const accessToken = useSelector(state => state.auth.accessToken);
  const tGUID = useSelector(state => state.auth.GUID);
  const dispatch = useDispatch();

  return useCallback(
    async (endpoint, params) => {
      // Accepts a NAV_PATHS descriptor, or a bare path string for any caller that still passes one.
      const target =
        typeof endpoint === "string" ? { path: endpoint, params: {} } : endpoint;

      // The descriptor's own params are spread last: nav_view (or job/backfill) decides what runs,
      // so a stray caller-supplied value must not be able to redirect it.
      const url =
        process.env.REACT_APP_API_ADMIN_URL +
        target.path +
        buildQueryString({ ...(params || {}), ...target.params });

      const request = await fetch(url, {
        method: "GET",
        // Chrome holds a second identical GET (another tab syncing the same fund) until the first
        // returns, so be-admin would never answer it 409. no-store skips the HTTP cache and that wait.
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          userid: tGUID,
          "x-id-token": "Bearer " + accessToken
        }
      });

      if (request.status === 401) {
        dispatch(authActions.autoSignOut());
        return { signedOut: true };
      }

      let response = null;
      try {
        response = await request.json();
      } catch (error) {
        response = null;
      }

      if (!request.ok) {
        // CustomErrorHandler returns { message, errors[] }; surface whichever it sent.
        const detail =
          response && (response.message || (response.errors && response.errors[0]));
        throw new NavRequestError(request.status, detail);
      }

      if (!response || !response.data) {
        throw new NavRequestError(request.status, null);
      }

      return {
        signedOut: false,
        data: response.data,
        total: response.total,
        // The sync answers 200 with status false when a chunk failed.
        status: response.status
      };
    },
    [accessToken, tGUID, dispatch]
  );
}

/**
 * Turns whatever a failed request threw into the props the error block renders.
 * Kept out of the views so every tab reports a failure the same way.
 */
export function describeError(error, intl, messages) {
  if (error && error.status) {
    const detail =
      error.detail || intl.formatMessage(messages.errorStatus, { status: error.status });
    return intl.formatMessage(messages.errorBodyWithDetail, { detail });
  }
  return intl.formatMessage(messages.errorBodyNetwork);
}

/**
 * Loads the fund picker options once per mount.
 *
 * /nav/funds returns only the funds that actually have NAV history (DISTINCT + INNER JOIN), which
 * is what the picker wants: selecting a fund whose table is empty would look like a broken page.
 * Funds with no history at all are named on the Sync Status tab instead.
 */
export function useFundOptions() {
  const requestNav = useNavRequest();
  const [fundOptions, setFundOptions] = useState([]);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [fundsFailed, setFundsFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadingFunds(true);
    setFundsFailed(false);

    requestNav(NAV_PATHS.navFunds)
      .then(result => {
        if (!active || result.signedOut) {
          return;
        }
        setFundOptions(
          result.data.map(fund => ({
            value: fund.fund_no,
            label: fund.fund_code + " - " + fund.fund_name
          }))
        );
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setFundOptions([]);
        setFundsFailed(true);
      })
      .finally(() => {
        if (active) {
          setLoadingFunds(false);
        }
      });

    return () => {
      active = false;
    };
  }, [requestNav]);

  return { fundOptions, loadingFunds, fundsFailed };
}
